import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '@app/core/config';
import { UserInfo } from '@app/core/models/user-info';
import { UtilsService } from '@app/core/services';
import { UserInfoService } from '@app/core/services/state';
import { RequestType, RequestTypeNew } from '@app/shared/types';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { ConfirmCodeOption } from '../models';
import { ConfirmCodeSecurityType, ConfirmCodeSendType } from '../types';
import { ConfirmCodeType } from '../types/confirm-code-type.enum';
import { TwoFactorAuthenticationType } from '@app/shared/types/two-factor-authentication.type';

@Injectable({
	providedIn: 'root'
})
export class RequestConfirmationService {

	private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

	private confirmCodeIsActiveSource = new BehaviorSubject<boolean>(false);
	public confirmCodeIsActive$ = this.confirmCodeIsActiveSource.asObservable();

	userInfoData: UserInfo;
	sendType: ConfirmCodeSendType;
	activeConfirmCodeType: ConfirmCodeType;
	option: ConfirmCodeOption;
	constructor(
		@Inject(APP_CONFIG) private appConfig: IAppConfig,
		private http: HttpClient,
		private utilsService: UtilsService,
		private userInfoService: UserInfoService
	) {
		this.getUserInfo();
	}

	updateActiveStatus(activated: boolean): void {
		this.confirmCodeIsActiveSource.next(activated);
	}

	sendCodeForRequestSecurityChange(): Observable<any> {
		const url = `${this.appConfig.apiEndpoint}/v2/security/setting/sendCode`;
		return this.http
			.post(url, { headers: this.headers })
			.pipe(map(this.utilsService.handleResponse));
	}

	sendCodeForRequestConfirm(requestType: RequestType, isNewCoreApi: boolean = false): Observable<any> {
		let url = `${this.appConfig.apiEndpoint}/v2/Security/Request/SendCode`;
		let payload: { requestType: RequestType } | { data: { requestType: RequestType } } = { requestType };
		if (isNewCoreApi) {
			url = `${this.appConfig.apiEndpoint}/new-core/security/request/send-code`;
			payload = {
				data: { requestType }
			};
		}
		return this.http
			.post(url, payload, { headers: this.headers })
			.pipe(map(this.utilsService.handleResponse));
	}

	sendCodeForRequestConfirmWithdrawCrypto(action: RequestTypeNew): Observable<any> {
		const url = `${this.appConfig.apiEndpoint}/v3/Security/Identification/SendCode`;
		return this.http
			.post(url, { action }, { headers: this.headers })
			.pipe(map(this.utilsService.handleResponse));
	}

	verifyCodeForRequestConfirmWithdrawCrypto(verifyHash: any): Observable<any> {
		const url = `${this.appConfig.apiEndpoint}/v3/Security/Identification/VerifyCode`;
		return this.http
			.post(url, verifyHash, { headers: this.headers })
			.pipe(map(this.utilsService.handleResponse));
	}

	sendCodeForRequestConfirmHybrid(data: { action: RequestTypeNew, authenticationType: TwoFactorAuthenticationType }): Observable<any> {
		const url = `${this.appConfig.apiEndpoint}/v3/Security/Identification/hybrid/SendCode`;
		return this.http
			.post(url, data, { headers: this.headers })
			.pipe(map(this.utilsService.handleResponse));
	}

	verifyCodeForRequestConfirmHybrid(verifyHash: any): Observable<any> {
		const url = `${this.appConfig.apiEndpoint}/v3/Security/Identification/hybrid/VerifyCode`;
		return this.http
			.post(url, verifyHash, { headers: this.headers })
			.pipe(map(this.utilsService.handleResponse));
	}

	getSendCodeType(option: ConfirmCodeOption): ConfirmCodeSendType {
		this.option = option;
		this.getConfirmCodeByOptionConfirmType(option);
		return this.sendType;
	}

	private getUserInfo(): void {
		this.userInfoService.getUserInfo$()
			.subscribe((userInfo: UserInfo) => {
				this.userInfoData = userInfo;
			});
	}

	private getConfirmCodeByOptionConfirmType(option: ConfirmCodeOption): void {

		const type: ConfirmCodeType = option.confirmType;

		switch (type) {
			case ConfirmCodeType.REQUEST_SECURITY_CHANGE:
				this.getChangeRequestSettingConfirmCodeData();
				break;

			case ConfirmCodeType.DEPOSIT_REQUEST:
			case ConfirmCodeType.WITHDRAWAL_REQUEST:
			case ConfirmCodeType.INTERNAL_TRANSFER_REQUEST:
			case ConfirmCodeType.BETWEEN_MY_WALLETS:
			case ConfirmCodeType.BUY_AND_SELL_REQUEST:
			case ConfirmCodeType.EXCHANGE_SERVICE_REQUEST:
			case ConfirmCodeType.VOUCHER:
			case ConfirmCodeType.POINT_WITHDRAWAL_REQUEST:
			case ConfirmCodeType.PEER_TO_PEER:
			case ConfirmCodeType.SMS_PANEL_CHARGE_REQUEST:
				this.getRequestConfirmCodeData();
				break;
		}
	}

	private getChangeRequestSettingConfirmCodeData(): void {
		this.sendType = ConfirmCodeSendType.Email;
		this.activeConfirmCodeType = ConfirmCodeType.REQUEST_SECURITY_CHANGE;
	}

	private getRequestConfirmCodeData(): void {

		const scecurityType = this.getRequestSecurityTypeFromUserInformation();

		this.getConfirmCodeDataBySecurityType(scecurityType);
	}

	private getRequestSecurityTypeFromUserInformation(): ConfirmCodeSecurityType {

		const userSecurityCode = this.userInfoData.securityCode;
		this.activeConfirmCodeType = this.option.confirmType;

		switch (this.option.confirmType) {
			case ConfirmCodeType.DEPOSIT_REQUEST:
				return userSecurityCode.deposit;

			case ConfirmCodeType.WITHDRAWAL_REQUEST:
				return userSecurityCode.withdrawal;

			case ConfirmCodeType.INTERNAL_TRANSFER_REQUEST:
				return userSecurityCode.send;

			case ConfirmCodeType.EXCHANGE_SERVICE_REQUEST:
				return userSecurityCode.exchange;

			case ConfirmCodeType.BETWEEN_MY_WALLETS:
				return userSecurityCode.exchange;

			case ConfirmCodeType.VOUCHER:
				return userSecurityCode.voucher;

			case ConfirmCodeType.POINT_WITHDRAWAL_REQUEST:
				return userSecurityCode.point;

			case ConfirmCodeType.SMS_PANEL_CHARGE_REQUEST:
				return userSecurityCode.smsPanelCharge;

			case ConfirmCodeType.PEER_TO_PEER:
				return userSecurityCode.peerToPeer;

			default:
				return null;
		}
	}

	private getConfirmCodeDataBySecurityType(type: ConfirmCodeSecurityType): void {
		switch (type) {
			case ConfirmCodeSecurityType.Disabled:
				this.sendType = ConfirmCodeSendType.disable;
				break;

			case ConfirmCodeSecurityType.SMS:
				this.sendType = ConfirmCodeSendType.SMS;
				break;

			case ConfirmCodeSecurityType.Email:
				this.sendType = ConfirmCodeSendType.Email;
				break;

			case ConfirmCodeSecurityType.TwoFactorAuthentication:
				this.sendType = ConfirmCodeSendType.TwoFactorAuthentication;
				break;
		}
	}
}
